import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { ILink } from 'tbk-components/src/components/Button';
import { ICareersLocation } from '../CareersLocationPage/CareersLocationHero';

/**
 * The tile item is connected to taxonomies, which we'll use to create the tiles.
 * @useTermData
 * @taxonomy location department business_unit job_type
 */
export interface ICareersLocationTileItems {
	/**
	 * Tile Item Heading
	 */
	heading?: string;
	/**
	 * Tile Item Text
	 * @control richtext
	 */
	text?: string;
	/**
	 * Tile Item Heading
	 */
	link?: ILink;
}

export interface ICareersLocationTileProps extends IBaseElementProps {
	/**
	 * Lead in Heading
	 */
	leadInHeading?: string;
	/**
	 * Lead in Text
	 * @control richtext
	 */
	leadInText?: string;
	/**
	 *	Lead in Content Layout on Desktop
	 */
	leadInContentLayout?: 'Inline' | 'Column';
	/**
	 * Tiles Heading
	 */
	heading?: string;
	/**
	 *  Tile Style
	 */
	tileStyle?: 'outlined' | 'solid';
	/**
	 * Select one or more terms to display in the component.  We will calculate the correct number of jobs to show available
	 * for each term.
	 */
	tileItems?: ICareersLocationTileItems[];
	/**
	 * If you select an item to pivot on, we will only count careers that have that term.  When this component displays on the front
	 * end, the number of careers for the tiles in "Tile items" will only include careers that have the pivot term.  Note, the editor
	 * in the back end does not use the pivot term.  It is only in the front end that you will see the accurate data.
	 */
	pivotItem?: ICareersLocation;
}

/**
 * Careers Location Tile
 * @block
 * @icon media-spreadsheet
 */
const CareersLocationTile: React.FC<ICareersLocationTileProps> = ({
	leadInHeading,
	leadInText,
	leadInContentLayout = 'Inline',
	heading,
	tileStyle,
	tileItems = [],
	pivotItem,
	id,
	className,
	classNames = [],
}) => {
	const ref = useRef(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const leadInText = ref.current.querySelector(
			'.calian-location-tile__lead-in--text',
		);

		if (leadInText) {
			if (leadInText.innerHTML === '<p><br></p>') {
				leadInText.innerHTML = '';
			}
		}
	}, []);
	return (
		<div
			id={id}
			className={className || cn(
				'calian-location-tile',
				tileStyle === 'solid' && 'calian-location-tile--solid',
				...classNames,
			)}
			ref={ref}
		>
			<div className="calian-location-tile__container">
				{leadInHeading && leadInText && (
					<div
						className={cn(
							'calian-location-tile__lead-in row',
							leadInContentLayout === 'Column' && 'column-layout',
						)}
					>
						<div className="col-12 col-xl-5">
							<h2 className="calian-location-tile__lead-in--heading">
								{leadInHeading}
							</h2>
						</div>
						<div className="col-12 col-xl-7">
							<div className="calian-location-tile__lead-in--text">
								<p
									dangerouslySetInnerHTML={{
										__html: leadInText,
									}}
								/>
							</div>
						</div>
					</div>
				)}
				{heading && (
					<h3 className="calian-location-tile__heading">{heading}</h3>
				)}
				{tileItems?.length > 0 && (
					<ul className="calian-location-tile__items">
						{tileItems.map(({ heading, text, link }, index) => {
							return (
								<li
									className="calian-location-tile__item"
									key={index}
								>
									{link?.href ? (
										<a
											className="calian-location-tile__item-wrapper"
											href={link.href}
											target={link.target || '_self'}
											rel={
												link.target
													? 'noopener noreferrer'
													: 'noopener'
											}
										>
											{heading && (
												<h4 className="calian-location-tile__tile-heading">
													{heading}
												</h4>
											)}
											{text && (
												<div
													className="calian-location-tile__tile-text"
													key={text}
													dangerouslySetInnerHTML={{
														__html: text,
													}}
												/>
											)}
										</a>
									) : (
										<div className="calian-location-tile__item-wrapper">
											{heading && (
												<h4 className="calian-location-tile__tile-heading">
													{heading}
												</h4>
											)}
											{text && (
												<div
													className="calian-location-tile__tile-text"
													dangerouslySetInnerHTML={{
														__html: text,
													}}
												/>
											)}
										</div>
									)}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</div>
	);
};

export default CareersLocationTile;
